import { useCallback, useState } from "react";
import { useAuthFetch } from "../utils/authFetch";

/**
 * useFeedbackSubmit owns the submit + loading/error/success state for the
 * ModernUI feedback widget. It posts to POST /api/v1/feedback via the
 * auth-aware fetch (so 401 session-expiry is handled globally) and includes
 * the X-Requested-With CSRF header the endpoint requires.
 *
 * US2090203: comment is the required field and thumb is optional — a null/absent
 * thumb is sent as "" (unrated). The submitter's identity is NOT part of the
 * payload: the handler reads it from the session, so nothing here can name or
 * disclaim a submitter.
 *
 * @returns {{
 *   submit: (payload: { thumb?: string|null, comment: string }) => Promise<boolean>,
 *   submitting: boolean,
 *   error: string|null,
 *   success: boolean
 * }}
 */
const useFeedbackSubmit = () => {
  const fetchWithAuth = useAuthFetch();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(
    // The `= {}` default matters: without it a bare submit() throws a destructuring
    // TypeError, which surfaces as an unhandled promise rejection rather than the
    // component's error state.
    async ({ thumb = null, comment = "" } = {}) => {
      setSubmitting(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await fetchWithAuth("/api/v1/feedback", {
          method: "POST",
          headers: {
            // The endpoint requires this custom header for CSRF protection.
            // authFetch does not send it by default, so add it explicitly.
            "X-Requested-With": "XMLHttpRequest",
          },
          // thumb is optional: normalize null/undefined to "" so the server sees
          // an explicitly unrated submission rather than a null it must special-case.
          body: JSON.stringify({ thumb: thumb || "", comment }),
        });

        if (!response.ok) {
          let message = "Unable to submit feedback. Please try again.";
          try {
            const data = await response.json();
            if (data && data.error && data.error.message) {
              message = data.error.message;
            }
          } catch (parseErr) {
            // Non-JSON error body; keep the default message.
          }
          setError(message);
          return false;
        }

        setSuccess(true);
        return true;
      } catch (err) {
        // 401 is already handled globally by useAuthFetch; surface a generic
        // message for network/other errors so the modal stays open.
        setError("Unable to submit feedback. Please try again.");
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [fetchWithAuth]
  );

  // US2090203: `error` otherwise survives until the START of the next submit, so a modal
  // reopened after a failure renders the previous attempt's message against an empty
  // form. That was cosmetic before; this story pointed aria-invalid="true" and
  // aria-describedby at that message, so it became a screen reader announcing a blank
  // required field as invalid, citing an attempt the user had already abandoned.
  //
  // Deliberately clears `error` only. `success` drives ModernHeader's toast timer and
  // nonce, and resetting it here would reach into that sequencing for no reason.
  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { submit, submitting, error, success, reset };
};

export default useFeedbackSubmit;
